import { Box, Stack, TextField, Typography } from '@mui/material';
import {
  SMS_SESSION_POLICY_MAX_NUMBER,
  SmsSessionGroupPolicy,
} from '@provision/conversation-types';
import { useRef, useState } from 'react';
import { SelectMenu } from 'src/components/SelectMenu';
import { SettingsSectionRow } from 'src/components/SettingsSection';
import {
  IDLE_TIMEOUT_UNIT_OPTIONS,
  IdleTimeoutUnit,
  maxIdleTimeoutAmount,
  parsePolicyNumber,
  toIdleSeconds,
  toIdleTimeoutInput,
} from './sms-session-timer-fields.utils';

const wholeNumberError = (max: number, unit?: IdleTimeoutUnit): string =>
  `Must be a whole number from 0 to ${max}${unit ? ` ${unit}` : ''}`;

const copyFieldLabelSx = { mb: 0.5 } as const;

const IdleTimeoutRow = ({
  idleSeconds,
  onSave,
}: {
  idleSeconds: number;
  onSave: (idleSeconds: number) => Promise<void>;
}) => {
  const initial = toIdleTimeoutInput(idleSeconds);
  const [amount, setAmount] = useState(String(initial.amount));
  const [unit, setUnit] = useState(initial.unit);
  const [error, setError] = useState<string | null>(null);
  const unitMenuOpen = useRef(false);

  const save = async (nextAmount: string, nextUnit: IdleTimeoutUnit): Promise<void> => {
    const seconds = toIdleSeconds(nextAmount, nextUnit);
    if (seconds === null) {
      setError(wholeNumberError(maxIdleTimeoutAmount(nextUnit), nextUnit));
      return;
    }
    setError(null);
    await onSave(seconds);
  };

  return (
    <SettingsSectionRow
      title="Idle timeout"
      secondary="Client silence before an idle nudge is sent. 0 never times out. The scheduler fires on minute boundaries, so a value is rounded up to the next whole minute."
      control={
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            size="small"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={() => {
              // The unit menu took focus: its pick saves the amount too, as one write.
              if (!unitMenuOpen.current) {
                void save(amount, unit);
              }
            }}
            error={!!error}
            helperText={error}
            inputProps={{ 'aria-label': 'Idle timeout', min: 0, step: 1 }}
            sx={{ width: 96 }}
          />
          {/* A press must not move focus, or the amount would save before the unit pick. */}
          <Box onMouseDown={(event) => event.preventDefault()}>
            <SelectMenu
              label="Idle timeout unit"
              options={IDLE_TIMEOUT_UNIT_OPTIONS}
              value={unit}
              onChange={(next) => {
                const nextUnit = next as IdleTimeoutUnit;
                setUnit(nextUnit);
                void save(amount, nextUnit);
              }}
              onOpenChange={(open) => {
                unitMenuOpen.current = open;
              }}
              menuWidth="trigger"
              sx={{ width: 112 }}
            />
          </Box>
        </Stack>
      }
    />
  );
};

const NudgeAttemptsRow = ({
  nudgeAttempts,
  onSave,
}: {
  nudgeAttempts: number;
  onSave: (nudgeAttempts: number) => Promise<void>;
}) => {
  const [value, setValue] = useState(String(nudgeAttempts));
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    const parsed = parsePolicyNumber(value);
    if (parsed === null) {
      setError(wholeNumberError(SMS_SESSION_POLICY_MAX_NUMBER));
      return;
    }
    setError(null);
    await onSave(parsed);
  };

  return (
    <SettingsSectionRow
      title="Nudge attempts before ending"
      secondary="Number of nudge messages to send before closing an idle session. Zero closes the session right away."
      control={
        <TextField
          size="small"
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => void save()}
          error={!!error}
          helperText={error}
          inputProps={{ 'aria-label': 'Nudge attempts before ending', min: 0, step: 1 }}
          sx={{ width: 96 }}
        />
      }
    />
  );
};

/** Empty is a valid "send nothing"; the text is trimmed before it is saved. */
const MessageField = ({
  label,
  initialValue,
  placeholder,
  disabled = false,
  onSave,
}: {
  label: string;
  initialValue: string;
  placeholder: string;
  disabled?: boolean;
  onSave: (value: string) => Promise<void>;
}) => {
  const [value, setValue] = useState(initialValue);

  const save = async (): Promise<void> => {
    const trimmed = value.trim();
    setValue(trimmed);
    await onSave(trimmed);
  };

  return (
    <Box>
      <Typography
        variant="body2"
        fontWeight={600}
        color="custom.OV.sys.onSurface"
        sx={copyFieldLabelSx}
      >
        {label}
      </Typography>
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => void save()}
        inputProps={{ 'aria-label': label }}
      />
    </Box>
  );
};

/** Mounted once per group, so the fields start from its entry; each saves on blur, a unit on pick. */
export const SmsSessionTimerFields = ({
  entry,
  onSave,
}: {
  entry: SmsSessionGroupPolicy;
  onSave: (patch: Partial<SmsSessionGroupPolicy>) => Promise<void>;
}) => (
  <>
    <IdleTimeoutRow
      idleSeconds={entry.idleSeconds}
      onSave={(idleSeconds) => onSave({ idleSeconds })}
    />
    <NudgeAttemptsRow
      nudgeAttempts={entry.nudgeAttempts}
      onSave={(nudgeAttempts) => onSave({ nudgeAttempts })}
    />
    <Box sx={{ px: 2.25, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <MessageField
        label="Idle nudge message"
        initialValue={entry.nudgeCopy}
        placeholder={entry.nudgeAttempts > 0 ? 'Write a nudge message' : 'No nudges are sent'}
        disabled={entry.nudgeAttempts === 0}
        onSave={(nudgeCopy) => onSave({ nudgeCopy })}
      />
      <MessageField
        label="Session-end message"
        initialValue={entry.goodbyeCopy}
        placeholder="No message. The session ends quietly."
        onSave={(goodbyeCopy) => onSave({ goodbyeCopy })}
      />
    </Box>
  </>
);
